import React from "react";

import { Spinner, ErrorBanner, Env } from "../shared";
import { useRecurringDeals, RecurringSeries } from "./api/useRecurringDeals";

/**
 * Monitor for every recurring deal series in one environment.
 *
 * The deals list shows what exists; this shows whether each series is still
 * producing. The generator materializes a 14-day horizon, so a series goes
 * quiet roughly a fortnight before its end date — `upcoming` is therefore the
 * early-warning column, and the reason this surface exists (SSA-1072).
 */

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

const HORIZON_DAYS = 14;

/** How many occurrences the pattern *should* produce inside the generator's
 *  horizon. A Tuesday-only series maxes out at 2, a Fri/Sat/Sun one at 6, so a
 *  raw count says nothing on its own — health is the ratio, not the number. */
function expectedUpcoming(days: RecurringSeries["recurrence_days"]): number {
  if (!days || days.length === 0) return 0;
  const wanted = new Set(days.map((d) => DAY_ORDER.indexOf(d)));
  let n = 0;
  const today = new Date();
  // From tomorrow through the end of the horizon: today's occurrence may
  // already have started, in which case it no longer counts as upcoming.
  for (let i = 1; i <= HORIZON_DAYS - 1; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    // JS getDay() is Sunday-first; DAY_ORDER is Monday-first.
    if (wanted.has((d.getDay() + 6) % 7)) n++;
  }
  return n;
}

function endsWithinHorizon(endDate: string | null): boolean {
  if (!endDate) return false;
  const end = new Date(`${endDate}T00:00:00`);
  const limit = new Date();
  limit.setDate(limit.getDate() + HORIZON_DAYS);
  return end <= limit;
}

/** Judged against the pattern, and separately against the end date, so a series
 *  that is genuinely finishing reads differently from one that has broken. */
function health(s: RecurringSeries): {
  label: string;
  tone: string;
  detail: string;
} {
  const expected = expectedUpcoming(s.recurrence_days);

  if (s.occurrences_upcoming === 0) {
    return {
      label: "Not producing",
      tone: "bg-red-950/50 border-red-800/60 text-red-300",
      detail: "No future occurrences materialized",
    };
  }
  if (endsWithinHorizon(s.recurrence_end_date)) {
    return {
      label: "Ending soon",
      tone: "bg-amber-950/50 border-amber-800/60 text-amber-300",
      detail: `Series ends ${s.recurrence_end_date}`,
    };
  }
  // One short is normal — today's occurrence drops out of "upcoming" the moment
  // it starts. Two or more short means the generator is not keeping up.
  if (expected > 0 && s.occurrences_upcoming < expected - 1) {
    return {
      label: "Behind",
      tone: "bg-amber-950/50 border-amber-800/60 text-amber-300",
      detail: `${s.occurrences_upcoming} upcoming, pattern implies ${expected}`,
    };
  }
  return {
    label: "Healthy",
    tone: "bg-emerald-950/50 border-emerald-800/60 text-emerald-300",
    detail: `${s.occurrences_upcoming} upcoming of ~${expected} in the horizon`,
  };
}

function formatDate(iso: string | null, tz: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...(tz ? { timeZone: tz } : {}),
    });
  } catch {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
}

function daysLabel(days: RecurringSeries["recurrence_days"]): string {
  if (!days || days.length === 0) return "—";
  const ordered = DAY_ORDER.filter((d) => days.includes(d));
  return ordered.map((d) => d[0].toUpperCase() + d.slice(1)).join(" · ");
}

export function RecurringDealsPanel({
  env,
  apiUrl,
  search = "",
  onOpenSeries,
}: {
  env: Env;
  apiUrl: string;
  /** Toolbar query. Matched against series title and venue name, mirroring the
   *  input's "Search by title or venue…" placeholder. The series endpoint takes
   *  no search parameter and the whole set is already in memory, so this filters
   *  client-side rather than refetching. */
  search?: string;
  onOpenSeries?: (seriesId: string) => void;
}): React.ReactElement {
  const { series, loading, error, refetch } = useRecurringDeals(apiUrl);

  const query = search.trim().toLowerCase();
  const matches = query
    ? series.filter(
        (s) =>
          s.title.toLowerCase().includes(query) ||
          s.business_name.toLowerCase().includes(query),
      )
    : series;

  // Worst first: a series that stopped producing is the thing worth seeing.
  const sorted = [...matches].sort(
    (a, b) =>
      a.occurrences_upcoming - b.occurrences_upcoming ||
      a.business_name.localeCompare(b.business_name),
  );

  const notProducing = sorted.filter(
    (s) => health(s).label === "Not producing",
  ).length;
  const windingDown = sorted.filter((s) =>
    ["Ending soon", "Behind"].includes(health(s).label),
  ).length;

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-gray-300">
          <span className="font-semibold text-white">{sorted.length}</span> series in{" "}
          <span className="uppercase">{env}</span>
          {query && <span className="text-gray-500"> · of {series.length}</span>}
        </span>
        {notProducing > 0 && (
          <span className="px-2.5 py-1 rounded-lg border text-xs font-semibold bg-red-950/50 border-red-800/60 text-red-300">
            {notProducing} not producing
          </span>
        )}
        {windingDown > 0 && (
          <span className="px-2.5 py-1 rounded-lg border text-xs font-semibold bg-amber-950/50 border-amber-800/60 text-amber-300">
            {windingDown} need attention
          </span>
        )}
        <button
          onClick={refetch}
          className="ml-auto px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-gray-600 transition-colors"
        >
          Refresh
        </button>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
        <div className="hidden sm:flex items-center gap-4 px-5 py-3 border-b border-gray-800">
          <div className="w-9 flex-shrink-0" />
          <p className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">Series</p>
          <p className="w-40 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider">Repeats</p>
          <p className="w-20 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right" title="Upcoming vs. what the repeat pattern implies for the next 14 days">Next 14d</p>
          <p className="w-24 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Next</p>
          <p className="w-28 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Ends</p>
          <p className="w-32 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</p>
        </div>

        {loading && sorted.length === 0 && (
          <div className="flex justify-center py-14">
            <Spinner />
          </div>
        )}

        {!loading && sorted.length === 0 && !error && (
          <p className="px-5 py-14 text-center text-sm text-gray-500">
            {query
              ? `No series match “${search.trim()}” in ${env}.`
              : `No recurring series in ${env}.`}
          </p>
        )}

        {sorted.map((s, i) => {
          const h = health(s);
          return (
            <button
              key={s.id}
              onClick={() => onOpenSeries?.(s.id)}
              className={`w-full text-left flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-800/50 transition-colors ${
                i < sorted.length - 1 ? "border-b border-gray-800" : ""
              }`}
            >
              <div className="w-9 h-9 rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {s.business_logo ? (
                  <img
                    src={s.business_logo}
                    alt=""
                    className="w-full h-full object-cover"
                    // A dead CDN URL must not leave a broken-image glyph in a
                    // monitoring table — fall back to the neutral tile.
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <span className="text-xs text-gray-500">
                    {s.business_name.slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{s.title}</p>
                <p className="text-xs text-gray-500 truncate">{s.business_name}</p>
              </div>

              <p className="hidden sm:block w-40 flex-shrink-0 text-xs text-gray-400 truncate">
                {daysLabel(s.recurrence_days)}
              </p>
              <p className="hidden sm:block w-20 flex-shrink-0 text-xs text-gray-400 text-right tabular-nums">
                {s.occurrences_upcoming}/{expectedUpcoming(s.recurrence_days)}
              </p>
              <p className="hidden sm:block w-24 flex-shrink-0 text-xs text-gray-300 text-right">
                {formatDate(s.next_occurrence, s.business_timezone)}
              </p>
              <p className="hidden sm:block w-28 flex-shrink-0 text-xs text-gray-400 text-right">
                {s.recurrence_end_date ?? "no end"}
              </p>

              <div className="w-32 flex-shrink-0">
                <span
                  title={h.detail}
                  className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold ${h.tone}`}
                >
                  {h.label}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-gray-600">
        The generator materializes 14 days ahead and runs every 6 hours. Health
        compares upcoming occurrences against what each repeat pattern implies
        for that window &mdash; a Tuesday-only series tops out at 2, so the count
        alone means nothing.
      </p>
    </div>
  );
}
